import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { buttonVariants } from '@/components/ui/button';
import { ArrowRight, Play, Sparkles, Video, Smartphone, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BackgroundAnimation } from '@/components/background-animation';
import { HomeFAQSection } from '@/components/landing/HomeFAQSection';
import { PricingSection } from '@/components/landing/PricingSection';
import WishlistSection from '@/components/landing/WishlistSection';
import { BlogSection } from '@/components/blog/BlogSection';
import { RelatedToolsSection } from '@/components/RelatedToolsSection';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { useAuth } from '@/context/auth-context';
import { apiFetch } from '@/lib/api';

interface SEOLandingPageProps {
  title: string;
  seoTitle?: string;
  description: string;
  keywords: string;
  heroTitle: string;
  heroSubtitle: string;
  features: {
    title: string;
    description: string;
    icon: React.ReactNode;
  }[];
  contentSnippet: string;
  videoMeta?: {
    name: string;
    description: string;
    thumbnailUrl: string;
    uploadDate: string;
    contentUrl: string;
    embedUrl: string;
  };
  currentTool?: string;
}

const SEOLandingPage: React.FC<SEOLandingPageProps> = ({
  title,
  seoTitle,
  description,
  keywords,
  heroTitle,
  heroSubtitle,
  features,
  contentSnippet,
  videoMeta,
  currentTool
}) => {
  const { user } = useAuth();
  const [wishlistedPlans, setWishlistedPlans] = useState<Set<string>>(new Set());
  const [selectedWishlistPlan, setSelectedWishlistPlan] = useState<string>('general');

  useEffect(() => {
    const saved = localStorage.getItem('fmg_wishlist');
    if (saved) setWishlistedPlans(new Set(JSON.parse(saved)));
  }, []);

  const handleSelectPlan = (planId: string) => {
    setSelectedWishlistPlan(planId);
  };

  const videoSchema = videoMeta ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": videoMeta.name,
    "description": videoMeta.description,
    "thumbnailUrl": videoMeta.thumbnailUrl,
    "uploadDate": videoMeta.uploadDate,
    "contentUrl": videoMeta.contentUrl,
    "embedUrl": videoMeta.embedUrl
  } : null;

  return (
    <div className="flex flex-col overflow-hidden">
      <SEO 
        title={seoTitle || title}
        description={description}
        keywords={keywords}
        schema={videoSchema}
      />
      <BackgroundAnimation />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Professional Content Creator Tool</span>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none">
              {heroTitle}
            </h1>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-sans mb-12">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans")}>
                Start Creating Free <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className={cn(buttonVariants({ variant: "outline", size: "lg" }), "rounded-full px-12 h-16 border-cyan-100 text-cyan-600 hover:bg-cyan-50 font-bold uppercase tracking-[0.3em] text-xs font-sans")}>
                See How It Works
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-24 bg-white/50 backdrop-blur-sm border-y border-zinc-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter">
                Why Creators Choose Our {title}
              </h2>
              <div className="prose prose-zinc prose-invert max-w-none mb-12">
                <p className="text-zinc-500 text-lg leading-relaxed">
                  {contentSnippet}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="group">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="font-bold text-zinc-950 uppercase tracking-tight mb-2">{feature.title}</h3>
                    <p className="text-zinc-500 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-cyan-400/5 blur-3xl rounded-[3rem]" />
              <div className="relative aspect-video rounded-[2rem] overflow-hidden border border-cyan-100 shadow-2xl bg-gradient-to-br from-white via-cyan-50/10 to-cyan-500/5 flex items-center justify-center group/showcase">
                <div className="absolute inset-0 bg-radial-gradient(ellipse_at_center,rgba(6,182,212,0.03),transparent_70%)" />
                <div className="text-center p-10 relative z-10">
                  <div className="w-20 h-20 bg-cyan-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-cyan-100/30 group-hover/showcase:scale-110 group-hover/showcase:bg-cyan-600 group-hover/showcase:text-white transition-all duration-300 shadow-lg shadow-cyan-600/5">
                    <Play className="w-8 h-8 text-cyan-600 transition-colors group-hover/showcase:text-white" />
                  </div>
                  <p className="text-cyan-950 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Preview Interactive Showcase</p>
                  <p className="text-zinc-500 text-[9px] font-medium uppercase tracking-wider">Experience the real-time simulation engine</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter">
            Dominate Short-Form Content
          </h2>
          <p className="text-zinc-500 max-w-3xl mx-auto mb-16 text-lg">
            Our specialized {title} is optimized for high-retention social media platforms. Join over 10,000 creators making viral content today.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-[2rem] bg-white border border-zinc-950/10 shadow-sm hover:shadow-xl transition-all">
              <TrendingUp className="w-10 h-10 text-cyan-600 mb-6" />
              <h3 className="text-xl font-bold mb-4 uppercase tracking-tight">Viral Potential</h3>
              <p className="text-zinc-500 text-sm">Satisfying physics content consistently ranks as a top-performing niche on TikTok and Reels.</p>
            </div>
            <div className="p-8 rounded-[2rem] bg-white border border-zinc-950/10 shadow-sm hover:shadow-xl transition-all">
              <Video className="w-10 h-10 text-cyan-600 mb-6" />
              <h3 className="text-xl font-bold mb-4 uppercase tracking-tight">4K Rendering</h3>
              <p className="text-zinc-500 text-sm">Export crystal-clear simulations up to 4K resolution, perfect for native high-quality uploads.</p>
            </div>
            <div className="p-8 rounded-[2rem] bg-white border border-zinc-950/10 shadow-sm hover:shadow-xl transition-all">
              <Smartphone className="w-10 h-10 text-cyan-600 mb-6" />
              <h3 className="text-xl font-bold mb-4 uppercase tracking-tight">Faceless Profits</h3>
              <p className="text-zinc-500 text-sm">Scale your brand without ever appearing on camera. The ultimate tool for faceless YouTube channels.</p>
            </div>
          </div>
        </div>
      </section>

      <PricingSection user={user} onSelectPlan={handleSelectPlan} />
      
      <WishlistSection selectedPlan={selectedWishlistPlan} />

      <HomeFAQSection />
      
      <BlogSection />

      <RelatedToolsSection currentTool={currentTool} />

      <DiscordCTA />

      <section className="py-24 bg-zinc-50 border-t border-zinc-100 text-zinc-950 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-cyan-500/5 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter">
            Ready to Start?
          </h2>
          <p className="text-zinc-500 text-lg mb-12">
            Get instant access to our professional creator studio and start generating {title} content that goes viral.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans shadow-lg shadow-cyan-600/10")}>
              Sign Up Free
            </Link>
            <Link to="/plans" className={cn(buttonVariants({ variant: "outline", size: "lg" }), "rounded-full px-12 h-16 border-cyan-100 text-cyan-600 hover:bg-cyan-50 font-bold uppercase tracking-[0.3em] text-xs font-sans")}>
              View Pro Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOLandingPage;
